import { fetchWithAuth } from "./fetchWithAuth";
// services/roles.ts
import {
  CreateRoleRequest,
  Role,
  RoleStatistics,
  PERMISSIONS_BY_CATEGORY,
} from "@/types/roles";

/* =========================
   Helpers de permisos
========================= */

// Catálogo plano de permisos válidos
const getAllValidPermissions = (): string[] => {
  const permissions: string[] = [];
  Object.values(PERMISSIONS_BY_CATEGORY).forEach((category) => {
    category.forEach((p) => {
      if (!permissions.includes(p.id)) {
        permissions.push(p.id);
      }
    });
  });
  return permissions;
};

const ALL_PERMISSIONS = getAllValidPermissions();

/**
 * Normaliza permisos que vienen del backend
 * Acepta: array | { "*": true } | { "module.*": true }
 * Devuelve: string[]
 */
export const normalizePermissions = (permissions: any): string[] => {
  if (!permissions) return [];

  // Array plano
  if (Array.isArray(permissions)) {
    return permissions;
  }

  // Objeto
  if (typeof permissions === "object") {
    // Full access
    if (permissions["*"] === true || permissions["all"] === true) {
      return [...ALL_PERMISSIONS];
    }

    const result = new Set<string>();

    Object.entries(permissions).forEach(([key, value]) => {
      if (value !== true) return;

      // Wildcard por módulo
      if (key.endsWith(".*")) {
        const prefix = key.replace(".*", ".");
        ALL_PERMISSIONS.filter((p) => p.startsWith(prefix)).forEach((p) =>
          result.add(p),
        );
      } else {
        result.add(key);
      }
    });

    return Array.from(result);
  }

  return [];
};

/**
 * Prepara permisos para backend
 * Backend acepta:
 *  - array de strings
 *  - { "*": true }
 */
export const preparePermissionsForBackend = (permissions: any) => {
  if (!permissions) return [];

  // Si viene full access, respétalo
  if (
    typeof permissions === "object" &&
    (permissions["*"] === true || permissions["all"] === true)
  ) {
    return { "*": true };
  }

  // Normalizar a array
  const normalized = normalizePermissions(permissions);

  return normalized;
};

/* =========================
   Service
========================= */

class RolesService {
  private baseURL = import.meta.env.VITE_AUTH_API_URL;

  private getHeaders(): HeadersInit {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /* =========================
     GET ROLES
  ========================= */
  async getRoles(slug: string): Promise<{ data: Role[] }> {
    const res = await fetchWithAuth(`${this.baseURL}/roles?slug=${slug}`, {
      headers: this.getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Error al obtener roles (${res.status})`);
    }

    const json = await res.json();

    const rolesData = json.data || json.body?.data || [];

    const roles = rolesData.map((role: any) => ({
      ...role, // ✅ preserva sales_quota y cualquier otro campo del backend
      permissions: normalizePermissions(role.permissions),
      sales_quota: role.sales_quota ?? role.salesQuota ?? undefined, // ✅ acepta ambas variantes del campo
    }));

    return { data: roles };
  }

  /* =========================
   CREATE ROLE ✅
========================= */
  async createRole(roleData: any): Promise<{ data: Role }> {
    console.log("🚀 [CREATE ROLE]", roleData);

    // Preparar permisos para backend
    const permissions = preparePermissionsForBackend(roleData.permissions);

    // Construir payload incluyendo userId si existe
    const payload = {
      slug: roleData.slug,
      userId: roleData.userId,
      name: roleData.name,
      description: roleData.description || "",
      permissions,
    };

    // Validar campos obligatorios
    if (!payload.slug || !payload.name || !payload.userId) {
      throw new Error("Faltan campos requeridos para crear el rol");
    }

    // Llamada POST al backend
    const res = await fetchWithAuth(`${this.baseURL}/roles`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });

    const text = await res.text();

    if (!res.ok) {
      throw new Error(text);
    }

    const json = JSON.parse(text);

    const responseData = json.data || json.body?.data;
    // Normalizar permisos del backend
    const role: Role = {
      ...responseData,
      permissions: normalizePermissions(responseData.permissions),
    };

    console.log("✅ [CREATE ROLE] OK");

    return { data: role };
  }

  /* =========================
     UPDATE ROLE
  ========================= */
  async updateRole(roleId: string, roleData: any): Promise<{ data: Role }> {
    const payload = {
      slug: roleData.slug,
      userId: roleData.userId,
      roleId: roleId,
      name: roleData.name,
      description: roleData.description,
      permissions: preparePermissionsForBackend(roleData.permissions),
    };

    const res = await fetchWithAuth(`${this.baseURL}/roles/${roleId}`, {
      method: "PUT",
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });

    const json = await res.json();

    const responseData = json.data || json.body?.data;
    const role: Role = {
      ...responseData,
      permissions: normalizePermissions(responseData.permissions),
    };

    return { data: role };
  }

  /* =========================
     DELETE ROLE
  ========================= */
  async deleteRole(
    roleId: string,
    slug: string,
    userId: string,
  ): Promise<void> {
    const payload = {
      slug,
      userId,
      roleId,
    };

    const res = await fetchWithAuth(`${this.baseURL}/roles/${roleId}`, {
      method: "DELETE",
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error("Error al eliminar rol");
    }
  }
}

export const rolesService = new RolesService();

